import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Box, Grid, Typography, IconButton, Tooltip, Modal } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosRoundedIcon from "@mui/icons-material/ArrowBackIosRounded";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { getReservationDetails } from "redux/actions/reservationAction";
import CommonButton from "components/commonButton/CommonButton";
import Loader from "components/loader/Loader";
import requestInstance from "services/Request";
import { API_ENDPOINT } from "services/Api";

import {
  formatCurrency,
  getDateFormat,
  getReservationDetailsObject,
} from "utils/utils";
import useModal from "hooks/useModal";
import {
  bookingDetailsData,
  bookingTime,
  fareBreakupData,
} from "../bookingDetails/BookingDetailsData";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ReservationAttachmentsDialog from "../bookingDetails/ReservationAttachmentsDialog";
import EditChargesDialog from "../bookingDetails/EditChargesDialog";

const ManageInvoiceBookingDetails = () => {
  const { open: attachmentsModal, toggle: setAttachmentsModal } = useModal();
  const { open: editChargesModal, toggle: setEditChargesModal } = useModal();
  const { open: paymentLinkModal, toggle: setPaymentLinkModal } = useModal();
  const { open: fareBreakupModal, toggle: setFareBreakupModal } = useModal();

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  const startDate = searchParams.get('startDate');
  const endDate = searchParams.get('endDate');

  const tripDetails = useSelector(
    (state) => state.getReservationDetails?.reservationDetails?.data
  );
  const reservationDataLoading = useSelector(
    (state) => state.getReservationDetails?.loading
  );
  const tripStatus = tripDetails?.status?.toLowerCase();
  const hasInvoice = tripDetails?.Invoices?.length > 0;
  const isBillToCard = tripDetails?.additionalInfo?.billingType == 1;
  const showPaymentLink = hasInvoice && isBillToCard;

  const [paymentLink, setPaymentLink] = useState("");
  const [paymentLinkLoading, setPaymentLinkLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const details = getReservationDetailsObject(tripDetails);

  const env = process.env.REACT_APP_B2B_API_BASE_URL;

  const fetchPaymentLink = useCallback(async () => {
    if (!id) return;
    try {
      setPaymentLinkLoading(true);
      const response = await requestInstance.get(
        `${env}${API_ENDPOINT.reservation.retrievePaymentLinkApi}/${id}`
      );
      setPaymentLink(response?.data?.data?.paymentLink || "");
    } catch (err) {
      setPaymentLink("");
    } finally {
      setPaymentLinkLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if(id){
      dispatch(getReservationDetails(id));
    }
  }, []);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(paymentLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleRegenerateLink = async () => {
    try {
      setPaymentLinkLoading(true);
      const response = await requestInstance.get(
        `${env}${API_ENDPOINT.reservation.generatePaymentLinkApi}/${id}`
      );
      setPaymentLink(response?.data?.data?.paymentLink || "");
    } catch (err) {
      setPaymentLink("");
    } finally {
      setPaymentLinkLoading(false);
    }
  };

  const filterValue = (key) => {
    const keys = ["Reporting Address", "Drop Address", "Via Address", "Landmark", "Special Instruction", "Trip Charges", "Flight No","Cancellation Reason"];

    if (key === "Special Instruction" && tripDetails?.DriverProfile) {
      return "";
    }
    else if (keys.includes(key)) {
      return "expandRow";
    }
  };

  const handleBackBtn = () => {
    navigate("/manage-invoice");
  };

  return (
    <>
      {reservationDataLoading ? (
        <Loader />
      ) : (
        <Box>
          <Grid container className="pb20 justifyBetween">
            <Box className="flex" alignItems={"center"}>
              <ArrowBackIosRoundedIcon
                className="font24 colorGrey"
                role="presentation"
                onClick={() => handleBackBtn()}
              />
              <Typography variant={"head4"} className="colorPrimary pl10">
                Booking ID {tripDetails?.tripId}
              </Typography>
            </Box>
            {tripStatus === "completed" && (
              <Box className="flex" gap={2}>
                <CommonButton
                  name="Edit"
                  iconType="edit"
                  iconPlace="front"
                  btnClass="btnSm"
                  disabled={hasInvoice}
                  onClick={() => setEditChargesModal()}
                />
                <CommonButton
                  name="Generate Invoice"
                  iconType="generate invoice"
                  iconPlace="front"
                  btnClass="btnSm"
                  disabled={hasInvoice}
                  onClick={() => navigate(
                    `/operation/billing/duty-slip?tripId=${tripDetails?.id}&companyId=${tripDetails?.companyId}&companyName=${tripDetails?.companyName}&cityId=${tripDetails?.city}&cityName=${tripDetails?.location}&status=pendingApproval&pageType=manageInvoice&startDate=${startDate}&endDate=${endDate}&page=1&rowPerPage=10&search=`
                  )}
                />
                {showPaymentLink && (
                  <CommonButton
                    name="Payment Link"
                    iconPlace="front"
                    btnClass="btnSm"
                    disabled={tripDetails?.Payments?.[0]?.status?.toLowerCase() === "success"}
                    onClick={() => { fetchPaymentLink(); setPaymentLinkModal(); }}
                  />
                )}
              </Box>
            )}
          </Grid>
          <Box className="dashedBorderBottom"></Box>

          <Grid container justifyContent={"center"}>
            <Grid item xs={11}>
              <Grid container className="ptb30 justifyBetween">
                <Typography variant="head5" className="fw600 colorBlack">
                  Booking Details&nbsp;
                  <span className="font14 fw400">
                    ({getDateFormat(tripDetails?.updatedAt, "fromNowFormat")})
                  </span>
                </Typography>

                {tripStatus === "completed" && (
                  <Box className="flex" alignItems="center" gap={1}>
                    <Typography variant={"head5"} className="colorPrimary fw600">
                      Total Trip Charges - ₹&nbsp;
                      {formatCurrency(tripDetails?.finalPrice)}
                    </Typography>
                    <Tooltip title="View Fare Breakup">
                      <IconButton size="small" onClick={setFareBreakupModal}>
                        <InfoOutlinedIcon sx={{ fontSize: 20, color: '#000' }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </Grid>

              <Grid container className="ptb20 mb20 bgLightOrange">
                {bookingTime &&
                  bookingTime(tripDetails).map((data, index) => (
                    <Grid
                      key={index}
                      item
                      xs={12}
                      sm={3}
                      sx={{
                        padding: { xs: "20px 0px 0px 20px", sm: "0px" },
                        justifyContent: { xs: "flex-start", sm: "center" },
                      }}
                      container
                      className="justifyCenter"
                    >
                      <Box className="flex">
                        <img src={data.img} alt="Logo" />
                      </Box>
                      <Typography variant={"body2"} className="colorBlack pl20">
                        {data.title || "NA"}
                      </Typography>
                    </Grid>
                  ))}
              </Grid>

              <Grid container className="mt40 mb20">
                {bookingDetailsData(tripDetails, details) &&
                  bookingDetailsData(tripDetails, details).map((value, key) => (
                    <Grid
                      key={key}
                      item
                      xs={12}
                      md={filterValue(value?.title, value?.status) ? 12 : 6}
                      sx={{ display: value?.title ? "flex" : "none" }}
                      container
                    >
                      <Grid
                        xs={4}
                        md={filterValue(value?.title, value?.status) ? 2 : 4}
                        item
                        className="p20 alignStart gridBorder"
                      >
                        <Typography variant={"body2"} className="colorGrey">
                          {value.title || "NA"}
                        </Typography>
                      </Grid>

                      <Grid
                        xs={8}
                        md={filterValue(value?.title, value?.status) ? 10 : 8}
                        item
                        flexWrap={"wrap"}
                        className="p20 alignStart gridBorder"
                      >
                        <Typography
                          variant={`${
                            value?.textType === "textBold" ? "head5" : "body2"
                          }`}
                          className={`pr10 ${
                            value?.textType === "textBold" && "fw700"
                          }`}
                        >
                          {value.data || "NA"}
                          {value.title === "Duty Type" && tripDetails?.additionalInfo?.isMultiDay && (
                            <span className="font12 fontNormal colorGrey">
                              {' (Multi-day)'}
                            </span>
                          )}
                        </Typography>
                        {value?.inclusive && (
                          <Typography variant={"body3"} className={"colorGrey"}>
                          (Exclusive of GST and Toll & Parking)
                          </Typography>
                        )}
                        {value.title === "Passenger Name" && value.isVip && (
                          <Typography variant={"body5"} className="vipBadge">
                            VIP
                          </Typography>
                        )}
                      </Grid>
                    </Grid>
                  ))}
              </Grid>

              {/* Toll & Parking Attachments Summary */}
              {tripStatus === "completed" && (
              <Grid className="mb30 mt20" container>
                <Grid item xs={12}>
                  <Grid container justifyContent="space-between" alignItems="center">
                    <Typography variant="head5" className="fw600 colorBlack">
                      Toll & Parking Attachments
                    </Typography>
                    <CommonButton
                      name="View / Manage Attachments"
                      iconType="view"
                      iconPlace="front"
                      btnClass="btnSm"
                      onClick={() => setAttachmentsModal()}
                    />
                  </Grid>
                  <Grid container className="mt10" alignItems="center" gap={3}>
                    <Typography variant="body2" className="colorGrey">
                      <AttachFileIcon className="font16" /> Tolls: {tripDetails?.additionalInfo?.attachments?.tolls?.length || 0} files
                    </Typography>
                    <Typography variant="body2" className="colorGrey">
                      <AttachFileIcon className="font16" /> Parking: {tripDetails?.additionalInfo?.attachments?.parking?.length || 0} files
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              )}

              {tripStatus === "completed" && (
                <img
                  src={tripDetails?.tripRouteImage?.url}
                  alt="map"
                  className="width100 height400"
                />
              )}
            </Grid>

          </Grid>

          <ReservationAttachmentsDialog
            open={attachmentsModal}
            handleClose={setAttachmentsModal}
            attachments={tripDetails?.additionalInfo?.attachments}
            tripId={tripDetails?.tripId}
            onSave={() => dispatch(getReservationDetails(id))}
            readOnly={tripDetails?.Invoices?.length > 0}
          />

          <EditChargesDialog
            open={editChargesModal}
            handleClose={setEditChargesModal}
            tripId={tripDetails?.id}
            tollCharges={tripDetails?.Payments?.[0]?.tripPricingDetails?.breakdown?.find(item => item.chargeType === "tollCharges")?.addedCharge ?? 0}
            parkingCharges={tripDetails?.Payments?.[0]?.tripPricingDetails?.breakdown?.find(item => item.chargeType === "parkingCharge")?.addedCharge ?? 0}
            garageToPickup={tripDetails?.additionalInfo?.kilometersOut ?? 0}
            dropToGarage={tripDetails?.additionalInfo?.kilometersIn ?? 0}
            onSave={() => dispatch(getReservationDetails(id))}
          />

          {/* Fare Breakup Dialog */}
          <Modal
            open={fareBreakupModal || false}
            onClose={setFareBreakupModal}
            className="customModal"
          >
            <Box
              className="modalContent textLeft"
              sx={{ width: 500, maxHeight: '80vh', overflowY: 'auto', p: 3, borderRadius: 2 }}
            >
              <Grid container justifyContent="space-between" alignItems="center" className="pb10">
                <Typography variant="head4" className="colorPrimary fw600">
                  Fare Breakup
                </Typography>
                <CloseIcon className="cursorPointer" onClick={setFareBreakupModal} />
              </Grid>
              <Box sx={{ mt: 1 }}>
                {fareBreakupData(tripDetails).map((item, idx) => (
                  <Grid container key={idx} sx={{ py: 0.8 }}>
                    <Grid item xs={7}>
                      <Typography variant="body2" className="colorGrey">{item.label}</Typography>
                    </Grid>
                    <Grid item xs={5} sx={{ textAlign: 'right' }}>
                      <Typography variant="body2" className="fw500">
                        {item.isDiscount ? `- ₹ ${formatCurrency(Math.abs(Number(item.value)))}` : `₹ ${formatCurrency(Number(item.value))}`}
                      </Typography>
                    </Grid>
                  </Grid>
                ))}
              </Box>
            </Box>
          </Modal>

          {/* Payment Link Dialog */}
          <Modal
            open={paymentLinkModal || false}
            onClose={setPaymentLinkModal}
            className="customModal"
          >
            <Box
              className="modalContent textLeft"
              sx={{ width: 650, minHeight: 200, p: 3, borderRadius: 2 }}
            >
              <Grid container justifyContent="space-between" alignItems="center" className="pb10">
                <Typography variant="head4" className="colorPrimary fw600">
                  Payment Link
                </Typography>
                <CloseIcon className="cursorPointer" onClick={setPaymentLinkModal} />
              </Grid>

              {paymentLinkLoading ? (
                <Box className="justifyCenter" sx={{ minHeight: 100 }}>
                  <Loader height="80px" />
                </Box>
              ) : paymentLink ? (
                <Box>
                  <Typography variant="body5" sx={{ color: '#f44336', fontStyle: 'italic', fontSize: '12px', mb: 1, fontWeight: 600 }}>
                    Note: Payment link is valid for 24 hrs
                  </Typography>
                  <Grid container alignItems="center" gap={1} flexWrap="nowrap">
                    <Grid item xs>
                      <Box
                        className="p10"
                        sx={{
                          backgroundColor: "#f5f5f5",
                          border: "1px solid #e0e0e0",
                          borderRadius: "8px",
                          wordBreak: "break-all",
                        }}
                      >
                        <Typography variant="body2" className="colorPrimary">
                          {paymentLink}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item>
                      <Tooltip title={copied ? "Copied!" : "Copy Link"}>
                        <IconButton onClick={handleCopyLink} size="small" color="primary">
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                  </Grid>
                  <Box className="mt20">
                    <CommonButton
                      name="Regenerate"
                      btnClass="btnSm"
                      onClick={handleRegenerateLink}
                    />
                  </Box>
                </Box>
              ) : (
                <Typography variant="body2" className="colorGrey">
                  No payment link available.
                </Typography>
              )}
            </Box>
          </Modal>
        </Box>
      )}
    </>
  );
};

export default ManageInvoiceBookingDetails;
